<template>
  <Content class="col-span-full" title="REALTIME_temperature">
    <!-- Top bar -->
    <div class="flex justify-end items-center mb-4">
      <div class="flex items-center gap-3" v-if="lastUpdated">
        <el-button
          type="text"
          icon="el-icon-refresh"
          @click="fetchTemperatures(true)"
          :loading="loading"
          class="p-0 text-blue-700"
          style="font-size: 16px"
        ></el-button>
        <span class="text-sm text-gray-600">
          {{ $t('LAST_UPDATED') }}: {{ formatTime(lastUpdated) }}
        </span>
      </div>
    </div>

    <!-- Chart section (shown when a vehicle row is clicked) -->
    <div v-if="selectedVehicleId" class="mb-6">
      <!-- Date/time range filter -->
      <div
        class="
          range-filter
          mb-3
          p-3
          bg-gray-50
          rounded-lg
          border border-gray-200
          flex flex-wrap
          gap-3
          items-end
        "
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 font-medium">
            {{ $t('DATE_RANGE') }}
          </label>
          <date-picker
            v-model="dateRange"
            color="#151515"
            class="el-input__inner"
            type="date"
            range
            format="YYYY-MM-DD"
            display-format="jYYYY/jMM/jDD"
            :clearable="true"
            :editable="false"
            :placeholder="$t('SELECT_DATE_RANGE')"
            :max="maxDate"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 font-medium">
            {{ $t('TIME_SELECTION') }}
          </label>
          <el-time-picker
            is-range
            v-model="timeRange"
            :range-separator="$t('TO')"
            :start-placeholder="$t('START_TIME')"
            :end-placeholder="$t('END_TIME')"
          />
        </div>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-search"
          :loading="chartLoading"
          @click="applyDateFilter"
        >
          نمایش نمودار
        </el-button>
        <el-button
          size="small"
          icon="el-icon-refresh-left"
          @click="resetToToday"
        >
          آخرین اطلاعات
        </el-button>
      </div>

      <!-- Chart component -->
      <TemperatureChart
        ref="chart"
        :chart-title="chartTitle"
        :chart-data="chartData"
        :chart-loading="chartLoading"
        :raw-temperature-data="rawTemperatureData"
        :arrival-datetimes="arrivalDatetimes"
        @close="closeChart"
      />
    </div>

    <!-- Vehicles table -->
    <el-table
      :empty-text="$t('NO_DATA')"
      :data="temperatures"
      :stripe="true"
      v-if="!error"
      style="width: 100%"
      class="temperature-table"
      :highlight-current-row="true"
      @row-click="onRowClick"
    >
      <el-table-column
        type="index"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('INDEX')"
        width="60"
      >
        <template slot-scope="scope">
          <div :data-title="$t('INDEX')">{{ scope.$index + 1 }}</div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('VEHICLE_TITLE')"
        width="160"
      >
        <template slot-scope="scope">
          <LicensePlate
            :license_plate="scope.row.license_plate"
            :chars="alphaList"
            class="cursor-pointer"
          />
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('DRIVER_TITLE')"
        width="180"
      >
        <template slot-scope="scope">
          <div :data-title="$t('DRIVER_TITLE')" class="cursor-pointer">
            {{ getDriverName(scope.row.driver) }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('MOBILE')"
        width="140"
      >
        <template slot-scope="scope">
          <div :data-title="$t('MOBILE')" class="text-gray-600 cursor-pointer">
            {{ (scope.row.driver && scope.row.driver.mobile) || '-' }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('temperature')"
        width="110"
      >
        <template slot-scope="scope">
          <div :data-title="$t('temperature')" class="cursor-pointer">
            <span
              class="flex items-center gap-1 ltr justify-end"
              v-if="scope.row.temperature"
            >
              <span
                class="inline-block w-2 h-2 rounded-full"
                :class="
                  scope.row.temperature > 0 ? 'bg-blue-400' : 'bg-red-400'
                "
              ></span>
              {{ scope.row.temperature }}°C
            </span>
            <span v-else class="text-gray-400">-</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('TIME')"
        width="200"
      >
        <template slot-scope="scope">
          <div
            :data-title="$t('TIME')"
            class="text-sm text-gray-600 cursor-pointer"
            v-html="formatTimestamp(scope.row.timestamp)"
          ></div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        width="80"
      >
        <template slot-scope="scope">
          <el-button
            size="mini"
            icon="el-icon-data-analysis"
            :type="
              selectedVehicleId === (scope.row.id || scope.row.vehicle_id)
                ? 'primary'
                : 'info'
            "
            @click.stop="onRowClick(scope.row)"
          >
            نمودار
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="error && !loading">
      <div class="flex items-center justify-center" style="min-height: 100px">
        <div class="text-lg mb-2">{{ $t('temperature_error') }}</div>
      </div>
    </div>
  </Content>
</template>

<script>
import {
  temperatureTracking,
  v3vehicleTemperatureTracking,
} from '@/api/reports'
import { licenseChars } from '@/api/global'
import LicensePlate from '@/components/cars/LicensePlate'
import TemperatureChart from '@/components/plans/TemperatureChart'
import { License } from '@/mixins/License'

export default {
  mixins: [License],
  components: { LicensePlate, TemperatureChart },

  data() {
    return {
      temperatures: [],
      loading: false,
      error: null,
      lastUpdated: null,
      intervalId: null,

      // Selected vehicle chart state
      selectedVehicleId: null,
      chartTitle: '',
      chartLoading: false,
      chartData: [],
      rawTemperatureData: [],
      arrivalDatetimes: [],

      // Date/time filter
      dateRange: null, // [startDateStr, endDateStr] from jalali date-picker
      timeRange: [new Date(new Date().setHours(0, 0, 0, 0)), new Date()],

      alphaList: [{ code: '', char: '', id: '' }],
    }
  },

  mounted() {
    this.loadAlphaList()
    this.startTracking()
  },

  beforeDestroy() {
    this.stopTracking()
  },

  methods: {
    async loadAlphaList() {
      try {
        const res = await licenseChars.api()
        if (res) this.alphaList = res
      } catch (e) {
        console.error('Error loading license chars:', e)
      }
    },

    async startTracking() {
      await this.fetchTemperatures()
      this.intervalId = setInterval(() => this.fetchTemperatures(), 60000)
    },

    stopTracking() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
    },

    async fetchTemperatures(isManualRefresh = false) {
      try {
        this.loading = true
        this.error = null
        const response = await temperatureTracking.api()
        if (response && Array.isArray(response)) {
          this.temperatures = response.map((item) => ({
            ...item,
            refreshing: false,
          }))
          this.lastUpdated = new Date()
          // Refresh open chart together with table data.
          if (this.selectedVehicleId) {
            await this.fetchVehicleChart(isManualRefresh)
          }
        } else {
          this.error = 'Invalid response format'
        }
      } catch (e) {
        console.error('Error fetching temperatures:', e)
        this.error = 'Failed to fetch temperature data'
      } finally {
        this.loading = false
      }
    },

    onRowClick(row) {
      const vehicleId = row.id || row.vehicle_id
      this.selectedVehicleId = vehicleId
      this.chartTitle = `${this.$t('temperature')} — ${
        this.License(row.license_plate, this.alphaList) || 'نامشخص'
      }`
      this.resetToToday()

      this.$nextTick(() => {
        const el = document.querySelector('.range-filter')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    resetToToday() {
      const today = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const todayStr = `${today.getFullYear()}-${pad(
        today.getMonth() + 1
      )}-${pad(today.getDate())}`
      this.dateRange = [todayStr, todayStr]
      this.timeRange = [new Date(new Date().setHours(0, 0, 0, 0)), new Date()]
      this.fetchVehicleChart(true)
    },

    applyDateFilter() {
      if (!this.dateRange || !this.dateRange[0] || !this.timeRange) {
        this.$message.warning('لطفاً بازه زمانی را کامل وارد کنید')
        return
      }
      this.fetchVehicleChart(true)
    },

    buildTimestamps() {
      const startDate = new Date(this.dateRange[0])
      startDate.setHours(
        this.timeRange[0].getHours(),
        this.timeRange[0].getMinutes(),
        this.timeRange[0].getSeconds()
      )
      const endDate = new Date(this.dateRange[1] || this.dateRange[0])
      endDate.setHours(
        this.timeRange[1].getHours(),
        this.timeRange[1].getMinutes(),
        this.timeRange[1].getSeconds()
      )
      return {
        startRange: this.convertToTimestamp(startDate),
        stopRange: this.convertToTimestamp(endDate),
      }
    },

    convertToTimestamp(datetimeValue) {
      // Keep the same behavior as OverviewMap timestamp conversion.
      const date = new Date(datetimeValue)
      return (date.getTime() - date.getTimezoneOffset() * 60000) / 1000
    },

    async fetchVehicleChart(showLoading = true) {
      if (!this.selectedVehicleId) return
      try {
        if (showLoading) this.chartLoading = true

        const { startRange, stopRange } = this.buildTimestamps()

        const response = await v3vehicleTemperatureTracking.api(
          this.selectedVehicleId,
          startRange,
          stopRange
        )

        if (
          response &&
          response.metric &&
          Array.isArray(response.metric.data)
        ) {
          this.rawTemperatureData = response.metric.data

          // TODO: remove mock — use real arrival_datetimes from API
          const realArrivals = response.arrival_datetimes || []
          // if (realArrivals.length) {
          //   this.arrivalDatetimes = realArrivals
          // } else if (response.metric.data.length >= 4) {
          //   const d = response.metric.data
          //   const q = Math.floor(d.length / 4)
          //   this.arrivalDatetimes = [d[q].datetime, d[q * 3].datetime]
          // } else {
          //   this.arrivalDatetimes = []
          // }
          this.arrivalDatetimes = realArrivals

          // Aggregate by minute
          const minuteMap = {}
          response.metric.data.forEach((item) => {
            const minuteTs = Math.floor(new Date(item.datetime).getTime() / 60000) * 60000
            if (!minuteMap[minuteTs]) minuteMap[minuteTs] = []
            minuteMap[minuteTs].push(parseFloat(item.value))
          })

          const chartData = Object.entries(minuteMap).map(
            ([minuteTs, values]) => ({
              x: Number(minuteTs),
              y: parseFloat(
                (values.reduce((a, b) => a + b, 0) / values.length).toFixed(3)
              ),
            })
          )
          chartData.sort((a, b) => a.x - b.x)

          this.chartData = [{ name: this.$t('temperature'), data: chartData }]
        } else {
          this.rawTemperatureData = []
          this.arrivalDatetimes = []
          this.chartData = []
          if (showLoading) {
            this.$notify.error({
              title: this.$i18n.t('NO_DATA'),
              duration: 4000,
              customClass: 'error_msg',
            })
          }
        }
      } catch (e) {
        console.error('Error fetching vehicle chart:', e)
        if (showLoading) {
          this.$notify.error({
            title: this.$i18n.t('NO_DATA'),
            duration: 4000,
            customClass: 'error_msg',
          })
        }
      } finally {
        if (showLoading) this.chartLoading = false
      }
    },

    closeChart() {
      this.selectedVehicleId = null
      this.chartData = []
      this.rawTemperatureData = []
      this.arrivalDatetimes = []
      this.chartTitle = ''
      if (this.$refs.chart) this.$refs.chart.resetState()
    },

    getDriverName(driver) {
      if (!driver) return '-'
      return (
        `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || '-'
      )
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '-'
      const d = new Date(timestamp)
      return `<div class="flex items-center space-x-1 justify-end flex-row-reverse">${d.toLocaleDateString(
        'fa-IR'
      )}<span class="text-gray-500 symbol ml-1">|</span><span>${d.toLocaleTimeString(
        'fa-IR'
      )}</span></div>`
    },

    formatTime(date) {
      return date.toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
  },

  computed: {
    maxDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
}
</script>

<style scoped>
.temperature-table :deep(.el-table__row) {
  cursor: pointer;
}

.temperature-table :deep(.el-table__row:hover td) {
  background-color: #f0f7ff !important;
}

.temperature-table :deep(.el-table__row.current-row td) {
  background-color: #e6f0ff !important;
}

.range-filter {
  border-radius: 8px;
}
</style>
